import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '@/api/client'
import { useAuthenticatedClient } from '@/api/queries'
import { useAuth } from '@/hooks/use-auth'
import type { components } from '@/api/schema'

// Staff chats with the store AI (USA-1560). POST /dashboard/staff-chats/send streams `data:` JSON
// events: chat and message (the saved question), status while a tool runs, delta, then done or error.

export type StaffChat = components['schemas']['StaffChatRead']
export type StaffChatDetail = components['schemas']['StaffChatDetailRead']
export type StaffChatMessage = components['schemas']['StaffChatMessageRead']

export type StaffChatEvent =
  | { type: 'chat'; chat: StaffChat }
  | { type: 'message'; message: StaffChatMessage }
  | { type: 'status'; text: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; message: StaffChatMessage }
  | { type: 'error'; message: string }

export const staffChatKeys = {
  all: ['staff-chats'] as const,
  list: ['staff-chats', 'list'] as const,
  detail: (chatId: string) => ['staff-chats', 'detail', chatId] as const,
}

export function useStaffChats() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: staffChatKeys.list,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff-chats')
      if (error) throw error
      return data
    },
  })
}

export function useStaffChat(chatId: string | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: staffChatKeys.detail(chatId ?? ''),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff-chats/{chat_uuid}', {
        params: { path: { chat_uuid: chatId ?? '' } },
      })
      if (error) throw error
      return data
    },
    enabled: !!chatId,
  })
}

export function useDeleteStaffChat() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (chatId: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/staff-chats/{chat_uuid}', {
        params: { path: { chat_uuid: chatId } },
      })
      if (error) throw error
    },
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: staffChatKeys.list })
      const previous = queryClient.getQueryData<StaffChat[]>(staffChatKeys.list)
      queryClient.setQueryData<StaffChat[]>(staffChatKeys.list, (chats) => chats?.filter((chat) => chat.uuid !== chatId))
      return { previous }
    },
    onError: (_error, _chatId, context) => {
      if (context?.previous) queryClient.setQueryData(staffChatKeys.list, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: staffChatKeys.list }),
  })
}

async function streamStaffChat(
  token: string,
  body: string,
  onEvent: (event: StaffChatEvent) => void,
  signal: AbortSignal,
) {
  const response = await fetch(`${API_BASE_URL}/dashboard/staff-chats/send`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body,
    signal,
  })
  if (!response.ok || !response.body) throw new Error(`Staff chat failed with HTTP ${response.status}`)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const { value, done } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      let end: number
      while ((end = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, end)
        buffer = buffer.slice(end + 2)
        const data = block
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n')
        if (data) onEvent(JSON.parse(data) as StaffChatEvent)
      }
      if (done) return
    }
  } finally {
    await reader.cancel().catch(() => undefined)
  }
}

const FAILED = "The AI couldn't answer that. Try sending it again."

/** The answer being written: what the AI is doing and the text so far. */
export type LiveAnswer = { chatId: string | null; question: string; status: string; text: string }

/** Ask the AI. The saved question and answer go into the chat's cache as they arrive; `live` is the
 * answer still streaming. `onChat` gets a new chat's id as soon as it exists. */
export function useAskStaffChat(onChat: (chatId: string) => void) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const [live, setLive] = useState<LiveAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const controller = useRef<AbortController | null>(null)
  useEffect(() => () => controller.current?.abort(), [])

  const ask = useCallback(
    async (question: string, chatId: string | undefined) => {
      controller.current?.abort()
      const abort = new AbortController()
      controller.current = abort
      setError(null)
      let current: LiveAnswer = { chatId: chatId ?? null, question, status: '', text: '' }
      let finished = false
      setLive(current)
      const update = (next: Partial<LiveAnswer>) => {
        current = { ...current, ...next }
        setLive(current)
      }
      const append = (id: string, message: StaffChatMessage) =>
        queryClient.setQueryData<StaffChatDetail>(staffChatKeys.detail(id), (chat) =>
          chat ? { ...chat, messages: [...(chat.messages ?? []), message] } : chat,
        )
      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Not signed in')
        await streamStaffChat(
          token,
          JSON.stringify({ body: question, chat_uuid: chatId ?? null }),
          (event) => {
            switch (event.type) {
              case 'chat':
                if (!queryClient.getQueryData(staffChatKeys.detail(event.chat.uuid)))
                  queryClient.setQueryData<StaffChatDetail>(staffChatKeys.detail(event.chat.uuid), {
                    ...event.chat,
                    messages: [],
                  })
                update({ chatId: event.chat.uuid })
                if (!chatId) onChat(event.chat.uuid)
                queryClient.setQueryData<StaffChat[]>(staffChatKeys.list, (chats) => [
                  event.chat,
                  ...(chats ?? []).filter((chat) => chat.uuid !== event.chat.uuid),
                ])
                break
              case 'message':
                if (current.chatId) append(current.chatId, event.message)
                break
              case 'status':
                update({ status: event.text })
                break
              case 'delta':
                update({ status: '', text: current.text + event.text })
                break
              case 'done':
                if (current.chatId) append(current.chatId, event.message)
                finished = true
                setLive(null)
                break
              case 'error':
                finished = true
                setError(event.message)
                setLive(null)
                break
            }
          },
          abort.signal,
        )
      } catch {
        // Reported below.
      } finally {
        if (!finished && !abort.signal.aborted) setError(FAILED)
        if (controller.current === abort) {
          controller.current = null
          setLive((value) => (value === current ? null : value))
        }
      }
    },
    [getAccessToken, onChat, queryClient],
  )

  return { ask, live, error, clearError: () => setError(null) }
}
